import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Search, Edit2, Trash2, Eye, EyeOff, RefreshCw,
  X, Layers, List, Tag, Crop, Upload,
} from 'lucide-react';
import kitchenApi, { resolveImageUrl } from '../services/kitchenApiService';
import BulkUploadPage from './BulkUploadPage';

const fmt$ = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
const getCatColor = (n) => {
  const m = { biryani:'#FF6B35', curries:'#3B82F6', curry:'#3B82F6', appetizers:'#10B981', desserts:'#EC4899', beverages:'#8B5CF6', bread:'#F59E0B', rice:'#F59E0B', sides:'#06B6D4' };
  return m[(n||'').toLowerCase()] || '#64748B';
};

// ── Menu Overview ─────────────────────────────────────────────────────────────
function MenuOverview() {
  const navigate   = useNavigate();
  const [loading,  setLoading]  = useState(true);
  const [items,    setItems]    = useState([]);
  const [cats,     setCats]     = useState([]);
  const [search,   setSearch]   = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const [ir, cr] = await Promise.all([
      kitchenApi.getMenuOverview(),
      kitchenApi.getMenuCategories(),
    ]);
    const itemList = ir.success ? (Array.isArray(ir.data) ? ir.data : []) : [];
    const catList  = cr.success ? (Array.isArray(cr.data) ? cr.data : []) : [];
    setItems(itemList);
    setCats(catList);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleAvailability = async (item) => {
    const newVal = !(item.is_available ?? true);
    await kitchenApi.toggleMenuItemAvailability(item.item_id || item.id, newVal);
    setItems(prev => prev.map(i => (i.item_id || i.id) === (item.item_id || item.id) ? { ...i, is_available: newVal } : i));
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this menu item?')) return;
    const res = await kitchenApi.deleteMenuItem(itemId);
    if (res.success) setItems(prev => prev.filter(i => (i.item_id || i.id) !== itemId));
  };

  const filtered = items.filter(item => {
    const matchSearch = !search || (item.item_name || item.name || '').toLowerCase().includes(search.toLowerCase());
    const matchCat    = catFilter === 'all' || String(item.category_id) === catFilter;
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? (item.is_available ?? true) : !(item.is_available ?? true));
    return matchSearch && matchCat && matchStatus;
  });

  const stats = {
    total:  items.length,
    active: items.filter(i => i.is_available ?? true).length,
    cats:   cats.length,
  };

  const QUICK = [
    { label: 'Add Item',       icon: Plus,   color: '#FF6B35', bg: '#FFF7F3', action: () => navigate('/kitchen/menu/add') },
    { label: 'Categories',     icon: Layers, color: '#3B82F6', bg: '#EFF6FF', action: () => navigate('/kitchen/menu/categories') },
    { label: 'All Items',      icon: List,   color: '#10B981', bg: '#ECFDF5', action: () => {} },
    { label: 'Bulk Upload',    icon: Upload, color: '#8B5CF6', bg: '#F5F3FF', action: () => navigate('/kitchen/menu/bulk-upload') },
  ];

  return (
    <div>
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Menu Management</h1>
          <p className="k-page-sub">{stats.total} items · {stats.active} active · {stats.cats} categories</p>
        </div>
        <div className="k-page-actions">
          <button className="k-btn k-btn-primary" onClick={() => navigate('/kitchen/menu/add')}>
            <Plus size={16} /> Add Item
          </button>
          <button className="k-btn-icon" onClick={() => loadData()} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {QUICK.map(({ label, icon: Icon, color, bg, action }) => (
          <button key={label} onClick={action} style={{ background: bg, border: `1px solid ${color}22`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', transition: 'box-shadow .15s' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
          >
            <div style={{ width: 32, height: 32, background: '#fff', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
              <Icon size={16} color={color} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="k-card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="k-input-group" style={{ flex: '1 1 220px', minWidth: 220 }}>
            <span className="k-input-icon"><Search size={16} /></span>
            <input className="k-input" placeholder="Search menu items…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="k-select" style={{ maxWidth: 200 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="all">All Categories</option>
            {cats.map(c => <option key={c.category_id || c.id} value={String(c.category_id || c.id)}>{c.name}</option>)}
          </select>
          <select className="k-select" style={{ maxWidth: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Items grid */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="k-card">
              <div className="k-skeleton" style={{ height: 140, borderRadius: 8, marginBottom: 12 }} />
              <div className="k-skeleton" style={{ height: 14, width: '70%', marginBottom: 8 }} />
              <div className="k-skeleton" style={{ height: 12, width: '50%' }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="k-card">
          <div className="k-empty">
            <div className="k-empty-icon">🍛</div>
            <div className="k-empty-title">{items.length === 0 ? 'No menu items yet' : 'No items match your filters'}</div>
            <div className="k-empty-sub">{items.length === 0 ? 'Add your first menu item to get started' : 'Try adjusting your search or filters'}</div>
            {items.length === 0 && (
              <button className="k-btn k-btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/kitchen/menu/add')}>
                <Plus size={16} /> Add First Item
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filtered.map(item => {
            const isAvail   = item.is_available ?? true;
            const catName   = item.category || cats.find(c => (c.category_id || c.id) === item.category_id)?.name || 'Uncategorized';
            const catColor  = getCatColor(catName);
            const isVeg     = (item.food_type || '').toLowerCase() === 'veg';
            return (
              <div key={item.item_id || item.id} className="k-card" style={{ padding: 0, overflow: 'hidden', opacity: isAvail ? 1 : .65 }}>
                {/* Image */}
                <div style={{ height: 140, background: `linear-gradient(135deg, ${catColor}22, ${catColor}11)`, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.image_url ? (
                    <img src={resolveImageUrl(item.image_url)} alt={item.item_name || item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 40 }}>🍛</span>
                  )}
                  {/* Food type dot */}
                  <div style={{ position: 'absolute', top: 10, left: 10, width: 18, height: 18, border: `2px solid ${isVeg ? '#16A34A' : '#DC2626'}`, borderRadius: 3, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: isVeg ? '#16A34A' : '#DC2626' }} />
                  </div>
                  {/* Availability toggle */}
                  <button
                    style={{ position: 'absolute', top: 8, right: 8, background: isAvail ? '#ECFDF5' : '#FEF2F2', border: `1px solid ${isAvail ? '#86EFAC' : '#FECACA'}`, borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: isAvail ? '#15803D' : '#DC2626', display: 'flex', alignItems: 'center', gap: 3 }}
                    onClick={() => toggleAvailability(item)}
                  >
                    {isAvail ? <Eye size={12} /> : <EyeOff size={12} />} {isAvail ? 'On' : 'Off'}
                  </button>
                </div>
                {/* Info */}
                <div style={{ padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--k-text)', flex: 1, marginRight: 8 }}>{item.item_name || item.name}</div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--k-primary)' }}>{fmt$(item.price)}</div>
                  </div>
                  {item.description && (
                    <p style={{ fontSize: 12, color: 'var(--k-text-sub)', marginBottom: 10, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: catColor, background: `${catColor}18`, padding: '2px 8px', borderRadius: 999 }}>{catName}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="k-btn-icon"
                        style={{ padding: 6 }}
                        onClick={() => navigate(`/kitchen/menu/edit/${item.item_id || item.id}`)}
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="k-btn-icon"
                        style={{ padding: 6, color: 'var(--k-danger)' }}
                        onClick={() => deleteItem(item.item_id || item.id)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Add / Edit Menu Item ──────────────────────────────────────────────────────
function MenuItemForm({ mode = 'add' }) {
  const navigate  = useNavigate();
  const { itemId } = useParams() || {};
  const [loading,  setLoading]  = useState(mode === 'edit');
  const [saving,   setSaving]   = useState(false);
  const [cats,     setCats]     = useState([]);
  const [toast,    setToast]    = useState('');
  const [toastType, setToastType] = useState('success');
  const imgRef = useRef(null);

  const emptyForm = { name: '', description: '', price: '', category_id: '', food_type: 'non_veg', is_available: true, preparation_time: '', calories: '', };
  const [form,  setForm]    = useState(emptyForm);
  const [img,   setImg]     = useState(null);
  const [imgPrev, setImgPrev] = useState('');
  const [errors, setErrors]  = useState({});
  const [cropModal, setCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const cropContainerRef = useRef(null);  // the div wrapping the image
  const cropImageRef = useRef(null);      // the <img> element
  const cropRef = useRef({ x: 0, y: 0, size: 0 }); // sync ref — no stale closures
  const [cropDisplay, setCropDisplay] = useState({ x: 0, y: 0, size: 0 }); // triggers re-render
  const cropDragRef = useRef(null); // { mode, startPos, startCrop }
  const [cropCursor, setCropCursor] = useState('crosshair');

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const showToast = (msg, type = 'success') => { 
    setToast(msg); 
    setToastType(type);
    setTimeout(() => setToast(''), 3000); 
  };

  useEffect(() => {
    kitchenApi.getMenuCategories().then(r => {
      if (r.success) setCats(Array.isArray(r.data) ? r.data : []);
    });
    if (mode === 'edit' && itemId) {
      kitchenApi.getMenuItemDetails(itemId).then(r => {
        if (r.success) {
          const d = r.data?.item || r.data;
          setForm({
            name:             d.item_name || d.name || '',
            description:      d.description || '',
            price:            String(d.price || ''),
            category_id:      String(d.category_id || ''),
            food_type:        d.food_type || 'non_veg',
            is_available:     d.is_available ?? true,
            preparation_time: String(d.preparation_time || d.prep_time_minutes || ''),
            calories:         String(d.calories || ''),
          });
          if (d.image_url) setImgPrev(resolveImageUrl(d.image_url));
        }
        setLoading(false);
      }).catch(() => {
        setLoading(false);
        showToast('Error loading menu item');
      });
    }
  }, [mode, itemId]);

  const handleImgChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImageToCrop(ev.target.result);
        setCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImg(null);
    setImgPrev('');
    if (imgRef.current) imgRef.current.value = '';
    showToast('Image removed');
  };

  // ── Crop helpers (image is FIXED; only the square selection moves/resizes) ──

  // Write both ref (sync) and state (renders) atomically
  const setCropState = (c) => {
    cropRef.current = c;
    setCropDisplay(c);
  };

  // Called once the image element has loaded — centres initial crop square
  const initCrop = () => {
    requestAnimationFrame(() => {
      const img = cropImageRef.current;
      if (!img || !img.naturalWidth) return;
      const w = img.clientWidth;
      const h = img.clientHeight;
      if (!w || !h) return;
      const size = Math.round(Math.min(w, h) * 0.78);
      setCropState({
        x: Math.round((w - size) / 2),
        y: Math.round((h - size) / 2),
        size,
      });
    });
  };

  // Mouse position relative to the crop container
  const getCropPos = (e) => {
    const rect = cropContainerRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const HANDLE_HIT = 16; // px hit-zone radius per corner

  // Returns 'tl'|'tr'|'bl'|'br'|'move'|null based on position vs crop box
  const getCropMode = (pos) => {
    const c = cropRef.current;
    const corners = [
      { mode: 'tl', cx: c.x,          cy: c.y          },
      { mode: 'tr', cx: c.x + c.size, cy: c.y          },
      { mode: 'bl', cx: c.x,          cy: c.y + c.size },
      { mode: 'br', cx: c.x + c.size, cy: c.y + c.size },
    ];
    for (const { mode, cx, cy } of corners) {
      if (Math.abs(pos.x - cx) <= HANDLE_HIT && Math.abs(pos.y - cy) <= HANDLE_HIT) return mode;
    }
    if (pos.x >= c.x && pos.x <= c.x + c.size && pos.y >= c.y && pos.y <= c.y + c.size) return 'move';
    return null;
  };

  const handleCropMouseDown = (e) => {
    e.preventDefault();
    if (!cropContainerRef.current) return;
    const pos = getCropPos(e);
    const mode = getCropMode(pos);
    if (!mode) return;
    cropDragRef.current = { mode, startPos: pos, startCrop: { ...cropRef.current } };
    setCropCursor(mode === 'move' ? 'grabbing' : 'crosshair');
  };

  const handleCropMouseMove = (e) => {
    if (!cropContainerRef.current) return;
    const pos = getCropPos(e);

    // Update cursor when not dragging
    if (!cropDragRef.current) {
      const mode = getCropMode(pos);
      const MAP = { tl: 'nwse-resize', br: 'nwse-resize', tr: 'nesw-resize', bl: 'nesw-resize', move: 'grab' };
      setCropCursor(MAP[mode] || 'crosshair');
      return;
    }

    const { mode, startPos, startCrop } = cropDragRef.current;
    const img = cropImageRef.current;
    if (!img) return;
    const imgW = img.clientWidth;
    const imgH = img.clientHeight;
    const MIN = 60;

    if (mode === 'move') {
      // Move: only the square shifts; image stays completely still
      const dx = pos.x - startPos.x;
      const dy = pos.y - startPos.y;
      setCropState({
        size: startCrop.size,
        x: Math.max(0, Math.min(startCrop.x + dx, imgW - startCrop.size)),
        y: Math.max(0, Math.min(startCrop.y + dy, imgH - startCrop.size)),
      });
      return;
    }

    // Resize: opposite corner is the fixed anchor
    let anchorX, anchorY;
    if      (mode === 'tl') { anchorX = startCrop.x + startCrop.size; anchorY = startCrop.y + startCrop.size; }
    else if (mode === 'tr') { anchorX = startCrop.x;                  anchorY = startCrop.y + startCrop.size; }
    else if (mode === 'bl') { anchorX = startCrop.x + startCrop.size; anchorY = startCrop.y; }
    else                    { anchorX = startCrop.x;                  anchorY = startCrop.y; } // br

    // Size = largest axis distance from anchor to mouse (keeps it square)
    let newSize = Math.max(MIN, Math.abs(pos.x - anchorX), Math.abs(pos.y - anchorY));

    // New top-left
    let newX = (mode === 'tl' || mode === 'bl') ? anchorX - newSize : anchorX;
    let newY = (mode === 'tl' || mode === 'tr') ? anchorY - newSize : anchorY;

    // Clamp to image bounds
    if (newX < 0) newX = 0;
    if (newY < 0) newY = 0;
    if (newX + newSize > imgW) newSize = imgW - newX;
    if (newY + newSize > imgH) newSize = imgH - newY;
    newSize = Math.max(MIN, newSize);

    setCropState({ x: newX, y: newY, size: newSize });
  };

  const handleCropMouseUp = () => {
    cropDragRef.current = null;
    setCropCursor('crosshair');
  };

  const applyCrop = () => {
    const img = cropImageRef.current;
    if (!img || !img.naturalWidth) return;
    const c = cropRef.current;

    // Map rendered px → natural image px for canvas drawImage
    const scaleX = img.naturalWidth  / img.clientWidth;
    const scaleY = img.naturalHeight / img.clientHeight;

    const canvas = document.createElement('canvas');
    const OUT = 800;
    canvas.width = OUT;
    canvas.height = OUT;
    canvas.getContext('2d').drawImage(
      img,
      c.x * scaleX, c.y * scaleY,       // source top-left in natural px
      c.size * scaleX, c.size * scaleY,  // source size in natural px
      0, 0, OUT, OUT                     // destination (full square output)
    );
    canvas.toBlob((blob) => {
      setImg(new File([blob], 'menu-item.jpg', { type: 'image/jpeg' }));
      setImgPrev(URL.createObjectURL(blob));
      setCropModal(false);
      showToast('Image cropped successfully');
    }, 'image/jpeg', 0.9);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Item name is required';
    if (!form.price || isNaN(form.price) || parseFloat(form.price) <= 0) e.price = 'Enter a valid price';
    if (!form.category_id) e.category_id = 'Select a category';
    setErrors(e);
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { 
      showToast('Please fix the errors before submitting', 'error');
      return; 
    }
    
    setErrors({});
    setSaving(true);
    
    try {
      const fd = new FormData();
      fd.append('item_name',    form.name.trim());
      fd.append('description',  form.description.trim());
      fd.append('price',        parseFloat(form.price).toString());
      fd.append('category_id',  form.category_id.toString());
      fd.append('food_type',    form.food_type);
      fd.append('is_available', form.is_available ? 'true' : 'false');
      if (form.preparation_time) fd.append('preparation_time', form.preparation_time);
      if (form.calories)         fd.append('calories', form.calories);

      if (img) {
        // Validate file type
        const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
        const MAX_SIZE_MB = 5;
        if (!ALLOWED_TYPES.includes(img.type)) {
          setErrors({ submit: 'Invalid file type. Please use JPG, PNG, WEBP, or GIF.' });
          showToast('Invalid file type', 'error');
          setSaving(false);
          return;
        }
        if (img.size > MAX_SIZE_MB * 1024 * 1024) {
          setErrors({ submit: `File too large. Maximum size is ${MAX_SIZE_MB}MB.` });
          showToast('File too large', 'error');
          setSaving(false);
          return;
        }
        console.log(`[MENU IMAGE] File selected: name=${img.name}, type=${img.type}, size=${img.size}`);
        fd.append('image', img);
        console.log('[MENU IMAGE] Uploading image with menu item update');
      }
      
      const res = mode === 'add'
        ? await kitchenApi.addMenuItem(fd)
        : await kitchenApi.updateMenuItem(itemId, fd);
      
      setSaving(false);
      
      if (res.success) { 
        showToast(mode === 'add' ? 'Item created!' : 'Changes saved!', 'success'); 
        setTimeout(() => navigate('/kitchen/menu'), 800); 
      } else {
        setErrors({ submit: res.error || 'Failed to save item.' });
        showToast(res.error || 'Failed to save item.', 'error');
      }
    } catch (error) {
      setSaving(false);
      setErrors({ submit: 'An unexpected error occurred.' });
      showToast('An unexpected error occurred.', 'error');
    }
  };

  if (loading) return <div className="k-loading-center"><div className="k-spinner" /></div>;

  const catName = cats.find(c => (c.category_id || c.id) === form.category_id)?.name || '';
  const catColor = getCatColor(catName);

  return (
    <div>
      {/* Header */}
      <div className="k-page-header">
        <div>
          <button className="k-btn k-btn-sm k-btn-ghost" onClick={() => navigate('/kitchen/menu')} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4, color: 'var(--k-primary)' }}>← Back</button>
          <h1 className="k-page-title">{mode === 'add' ? 'Add New Menu Item' : 'Edit Menu Item'}</h1>
          <p className="k-page-sub">{mode === 'add' ? 'Create a new dish in your menu' : `Editing "${form.name || 'Untitled'}"`}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Two-column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, marginBottom: 24 }}>
          {/* ─── LEFT COLUMN: MAIN INFO ─── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            
            {/* ✦ BASIC INFORMATION ✦ */}
            <div className="k-card">
              <div className="k-card-header" style={{ marginBottom: 18 }}>
                <div className="k-card-title">📝 Basic Information</div>
              </div>

              {/* Item Name */}
              <div className="k-form-group">
                <label className="k-label required">Item Name</label>
                <input
                  className={`k-input${errors.name ? ' k-input-error' : ''}`}
                  placeholder="e.g. Hyderabadi Chicken Biryani"
                  value={form.name}
                  onChange={e => { setF('name', e.target.value); if (errors.name) setErrors(er => ({ ...er, name: '' })); }}
                  style={{ fontSize: 15, fontWeight: 500 }}
                />
                {errors.name && <div className="k-error-text">✗ {errors.name}</div>}
              </div>

              {/* Description */}
              <div className="k-form-group">
                <label className="k-label">Description</label>
                <textarea
                  className="k-textarea"
                  placeholder="Tell customers about this dish. Include ingredients, preparation style, and any special notes..."
                  value={form.description}
                  onChange={e => setF('description', e.target.value)}
                  rows={4}
                  style={{ fontSize: 14, minHeight: 100 }}
                />
                <div style={{ fontSize: 11, color: 'var(--k-text-sub)', marginTop: 4 }}>{form.description.length}/500</div>
              </div>
            </div>

            {/* ✦ PRICING & CATEGORY ✦ */}
            <div className="k-card">
              <div className="k-card-header" style={{ marginBottom: 16 }}>
                <div className="k-card-title">💰 Pricing & Category</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Price */}
                <div className="k-form-group">
                  <label className="k-label required">Price ($)</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 12, top: 10, fontSize: 18, fontWeight: 700, color: 'var(--k-primary)' }}>$</span>
                    <input
                      className={`k-input${errors.price ? ' k-input-error' : ''}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={form.price}
                      onChange={e => { setF('price', e.target.value); if (errors.price) setErrors(er => ({ ...er, price: '' })); }}
                      style={{ paddingLeft: 32, fontSize: 16, fontWeight: 600 }}
                    />
                  </div>
                  {errors.price && <div className="k-error-text">✗ {errors.price}</div>}
                </div>

                {/* Category */}
                <div className="k-form-group">
                  <label className="k-label required">Category</label>
                  <select
                    className={`k-select${errors.category_id ? ' k-input-error' : ''}`}
                    value={form.category_id}
                    onChange={e => { setF('category_id', e.target.value); if (errors.category_id) setErrors(er => ({ ...er, category_id: '' })); }}
                    style={{ fontSize: 15 }}
                  >
                    <option value="">— Select Category —</option>
                    {cats.map(c => <option key={c.category_id || c.id} value={c.category_id || c.id}>{c.name}</option>)}
                  </select>
                  {errors.category_id && <div className="k-error-text">✗ {errors.category_id}</div>}
                </div>
              </div>
            </div>

            {/* ✦ NUTRITION & TIMING ✦ */}
            <div className="k-card">
              <div className="k-card-header" style={{ marginBottom: 16 }}>
                <div className="k-card-title">⏱️ Nutrition & Preparation</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Prep Time */}
                <div className="k-form-group">
                  <label className="k-label">Prep Time (minutes)</label>
                  <input
                    className="k-input"
                    type="number"
                    min="0"
                    placeholder="20"
                    value={form.preparation_time}
                    onChange={e => setF('preparation_time', e.target.value)}
                    style={{ fontSize: 15 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--k-text-sub)', marginTop: 4 }}>How long to prepare this item</div>
                </div>

                {/* Calories */}
                <div className="k-form-group">
                  <label className="k-label">Calories</label>
                  <input
                    className="k-input"
                    type="number"
                    min="0"
                    placeholder="650"
                    value={form.calories}
                    onChange={e => setF('calories', e.target.value)}
                    style={{ fontSize: 15 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--k-text-sub)', marginTop: 4 }}>Per serving (optional)</div>
                </div>
              </div>
            </div>

            {/* ✦ FOOD TYPE & AVAILABILITY ✦ */}
            <div className="k-card">
              <div className="k-card-header" style={{ marginBottom: 16 }}>
                <div className="k-card-title">🍽️ Food Type & Availability</div>
              </div>

              {/* Food Type buttons */}
              <div className="k-form-group" style={{ marginBottom: 14 }}>
                <label className="k-label">Food Type</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { v: 'veg',     l: '🥦 Vegetarian',  desc: 'No meat' },
                    { v: 'non_veg', l: '🍗 Non-Veg',     desc: 'Contains meat' },
                    { v: 'egg',     l: '🥚 Egg-Based',   desc: 'Contains eggs' }
                  ].map(({ v, l, desc }) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setF('food_type', v)}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: `2px solid ${form.food_type === v ? 'var(--k-primary)' : 'var(--k-border)'}`,
                        background: form.food_type === v ? 'var(--k-primary-light, #fff7f0)' : 'var(--k-bg)',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        color: form.food_type === v ? 'var(--k-primary)' : 'var(--k-text)',
                        transition: 'all .15s',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>{l.split(' ')[0]}</span>
                      <span style={{ fontSize: 10, opacity: .7 }}>{l.split(' ').slice(1).join(' ')}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Availability toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 14px',
                borderRadius: 10,
                background: 'var(--k-bg-secondary, #f8f9fa)',
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Available for Ordering</div>
                  <div style={{ fontSize: 12, color: 'var(--k-text-sub)', marginTop: 2 }}>Show this item on the menu</div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.is_available}
                    onChange={e => setF('is_available', e.target.checked)}
                    style={{ width: 20, height: 20, accentColor: 'var(--k-primary)', cursor: 'pointer' }}
                  />
                </label>
              </div>
            </div>

            {/* Error banner */}
            {errors.submit && (
              <div style={{
                padding: '12px 16px',
                borderRadius: 10,
                background: '#FEE2E2',
                border: '1px solid #FECACA',
                color: '#DC2626',
                fontSize: 13,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                ✗ {errors.submit}
              </div>
            )}
          </div>

          {/* ─── RIGHT COLUMN: IMAGE ─── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Image upload */}
            <div className="k-card" style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="k-card-title" style={{ marginBottom: 12, fontSize: 14 }}>📸 Item Image</div>
              <div
                onClick={() => !imgPrev && imgRef.current?.click()}
                style={{
                  cursor: imgPrev ? 'default' : 'pointer',
                  borderRadius: 12,
                  border: '2px dashed var(--k-border)',
                  padding: imgPrev ? 0 : '20px',
                  textAlign: 'center',
                  background: 'var(--k-bg-secondary, #f8f9fa)',
                  transition: 'all .2s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: imgPrev ? 'auto' : 200,
                  position: 'relative',
                }}
                onMouseEnter={e => !imgPrev && (e.currentTarget.style.borderColor = 'var(--k-primary)')}
                onMouseLeave={e => !imgPrev && (e.currentTarget.style.borderColor = 'var(--k-border)')}
              >
                {imgPrev ? (
                  <>
                    <img src={imgPrev} alt="preview" style={{ width: '100%', height: 'auto', borderRadius: 10, objectFit: 'cover' }} />
                    <div style={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8, 
                      display: 'flex', 
                      gap: 6,
                    }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); imgRef.current?.click(); }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          borderRadius: 6,
                          padding: 6,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                        title="Change image"
                      >
                        <Crop size={16} color="#64748B" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                        style={{
                          background: 'rgba(220, 38, 38, 0.95)',
                          border: '1px solid rgba(185, 28, 28, 0.2)',
                          borderRadius: 6,
                          padding: 6,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)',
                        }}
                        title="Remove image"
                      >
                        <X size={16} color="#fff" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 48, marginBottom: 8 }}>📷</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--k-text)', marginBottom: 4 }}>Click to upload image</div>
                    <div style={{ fontSize: 11, color: 'var(--k-text-sub)' }}>JPG, PNG (up to 5MB)</div>
                  </>
                )}
              </div>
              <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImgChange} />
            </div>

            {/* Category preview badge */}
            {form.category_id && catName && (
              <div className="k-card" style={{ padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--k-text-sub)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: .5, marginBottom: 8 }}>Category</div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: `${catColor}15`,
                  border: `1px solid ${catColor}40`,
                }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: catColor, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: catColor }}>{catName}</span>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="k-card" style={{ padding: 12, background: 'var(--k-bg-secondary, #f8f9fa)' }}>
              <div style={{ fontSize: 11, color: 'var(--k-text-sub)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: .5, marginBottom: 10 }}>Summary</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[
                  { l: 'Price', v: fmt$(form.price) },
                  { l: 'Type', v: form.food_type.replace('_', ' ').toUpperCase() },
                  { l: 'Prep', v: form.preparation_time ? `${form.preparation_time}min` : '—' },
                  { l: 'Cal', v: form.calories ? `${form.calories}kcal` : '—' },
                  { l: 'Status', v: form.is_available ? '✓ Active' : '✗ Inactive' },
                ].map(({ l, v }) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, paddingBottom: 6, borderBottom: '1px solid var(--k-border-light)' }}>
                    <span style={{ color: 'var(--k-text-sub)', fontWeight: 600 }}>{l}</span>
                    <span style={{ fontWeight: 700, color: 'var(--k-text)' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ─── ACTION BUTTONS ─── */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid var(--k-border)' }}>
          <button
            type="button"
            className="k-btn k-btn-ghost"
            onClick={() => navigate('/kitchen/menu')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="k-btn k-btn-primary"
            disabled={saving}
            style={{ minWidth: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {saving ? (
              <>
                <span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff', width: 14, height: 14 }} />
                {mode === 'add' ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              <>
                {mode === 'add' ? '✓ Create Item' : '✓ Save Changes'}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Toast */}
      {toast && (
        <div className="k-toast-container">
          <div className={`k-toast ${toastType}`}>
            {toastType === 'success' ? '✓' : toastType === 'error' ? '✗' : '⚠'} {toast}
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {cropModal && imageToCrop && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16,
            maxWidth: 820, width: '100%',
            maxHeight: '92vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          }}>
            {/* Header */}
            <div style={{
              padding: '18px 24px', borderBottom: '1px solid var(--k-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Crop Image</h3>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--k-text-sub)' }}>
                  Drag inside the square to move it · Drag any corner to resize · Image stays fixed
                </p>
              </div>
              <button
                onClick={() => setCropModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 6, display: 'flex', alignItems: 'center' }}
              >
                <X size={20} color="#64748B" />
              </button>
            </div>

            {/* Crop canvas area */}
            <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {/* 
                THE IMAGE IS ALWAYS STATIONARY.
                Only the square selection frame moves and resizes.
                All pointer events are handled by the container div.
              */}
              <div
                ref={cropContainerRef}
                style={{
                  position: 'relative',
                  lineHeight: 0,          // prevents gap below the image
                  userSelect: 'none',
                  cursor: cropCursor,
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: '#111',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                }}
                onMouseDown={handleCropMouseDown}
                onMouseMove={handleCropMouseMove}
                onMouseUp={handleCropMouseUp}
                onMouseLeave={handleCropMouseUp}
              >
                {/* The image — completely stationary, never moves */}
                <img
                  ref={cropImageRef}
                  src={imageToCrop}
                  alt="Crop"
                  draggable={false}
                  onLoad={initCrop}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    maxHeight: '60vh',
                    objectFit: 'contain',
                    pointerEvents: 'none',  // image receives NO pointer events
                    userSelect: 'none',
                  }}
                />

                {/* Dark overlay outside the crop square — using box-shadow trick */}
                {cropDisplay.size > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: cropDisplay.x,
                      top: cropDisplay.y,
                      width: cropDisplay.size,
                      height: cropDisplay.size,
                      // The enormous box-shadow creates the dark surround
                      boxShadow: '0 0 0 9999px rgba(0,0,0,0.58)',
                      border: '2px solid rgba(255,255,255,0.9)',
                      boxSizing: 'border-box',
                      pointerEvents: 'none',  // events pass through to container
                    }}
                  >
                    {/* Rule-of-thirds grid lines */}
                    <div style={{
                      position: 'absolute', inset: 0, pointerEvents: 'none',
                      backgroundImage: `
                        linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)
                      `,
                      backgroundSize: `${Math.round(cropDisplay.size / 3)}px ${Math.round(cropDisplay.size / 3)}px`,
                    }} />

                    {/* Corner handles — visual only (events go to container) */}
                    {[
                      { key: 'tl', style: { top: -7, left: -7 } },
                      { key: 'tr', style: { top: -7, right: -7 } },
                      { key: 'bl', style: { bottom: -7, left: -7 } },
                      { key: 'br', style: { bottom: -7, right: -7 } },
                    ].map(({ key, style }) => (
                      <div
                        key={key}
                        style={{
                          position: 'absolute',
                          width: 15, height: 15,
                          background: '#fff',
                          border: '2.5px solid #FF6B35',
                          borderRadius: 3,
                          boxShadow: '0 1px 5px rgba(0,0,0,0.55)',
                          pointerEvents: 'none',
                          ...style,
                        }}
                      />
                    ))}

                    {/* Center indicator */}
                    <div style={{
                      position: 'absolute',
                      top: '50%', left: '50%',
                      transform: 'translate(-50%,-50%)',
                      width: 24, height: 24,
                      borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.5)',
                      pointerEvents: 'none',
                    }} />
                  </div>
                )}
              </div>

              {/* Size info */}
              {cropDisplay.size > 0 && (
                <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--k-text-sub)', textAlign: 'center' }}>
                  Crop area: {Math.round(cropDisplay.size)} × {Math.round(cropDisplay.size)} px
                  &nbsp;·&nbsp; 1 : 1 square
                </p>
              )}
            </div>

            {/* Footer actions */}
            <div style={{
              padding: '14px 24px', borderTop: '1px solid var(--k-border)',
              display: 'flex', gap: 10, justifyContent: 'flex-end',
              flexShrink: 0,
            }}>
              <button type="button" onClick={() => setCropModal(false)} className="k-btn k-btn-ghost">
                Cancel
              </button>
              <button type="button" onClick={applyCrop} className="k-btn k-btn-primary">
                <Crop size={15} /> Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Manage Categories ─────────────────────────────────────────────────────────
function ManageCategories() {
  const navigate   = useNavigate();
  const [cats,     setCats]    = useState([]);
  const [loading,  setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCat,  setEditCat] = useState(null);
  const [saving,   setSaving]  = useState(false);
  const [form,     setForm]    = useState({ name: '', description: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await kitchenApi.getMenuCategories();
    if (r.success) setCats(Array.isArray(r.data) ? r.data : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm({ name: '', description: '' }); setEditCat(null); setShowForm(true); };
  const openEdit = (cat) => { setForm({ name: cat.name, description: cat.description || '' }); setEditCat(cat); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const res = editCat
      ? await kitchenApi.updateMenuCategory(editCat.category_id || editCat.id, form)
      : await kitchenApi.createMenuCategory(form);
    setSaving(false);
    if (res.success) { setShowForm(false); load(); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    await kitchenApi.deleteMenuCategory(cat.category_id || cat.id);
    load();
  };

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <div className="k-page-header">
        <div>
          <button className="k-btn k-btn-sm k-btn-ghost" onClick={() => navigate('/kitchen/menu')} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>← Back</button>
          <h1 className="k-page-title">Manage Categories</h1>
        </div>
        <button className="k-btn k-btn-primary" onClick={openAdd}><Plus size={16} /> Add Category</button>
      </div>

      {loading ? (
        <div className="k-loading-center"><div className="k-spinner" /></div>
      ) : cats.length === 0 ? (
        <div className="k-card"><div className="k-empty"><div className="k-empty-icon">🗂️</div><div className="k-empty-title">No categories yet</div><button className="k-btn k-btn-primary" style={{ marginTop: 16 }} onClick={openAdd}>Add First Category</button></div></div>
      ) : (
        <div className="k-card">
          {cats.map((cat, i) => (
            <div key={cat.category_id || cat.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: i < cats.length - 1 ? '1px solid var(--k-border-light)' : 'none' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${getCatColor(cat.name)}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🍛</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{cat.name}</div>
                {cat.description && <div style={{ fontSize: 12, color: 'var(--k-text-sub)' }}>{cat.description}</div>}
              </div>
              <button className="k-btn-icon" style={{ padding: 6 }} onClick={() => openEdit(cat)}><Edit2 size={14} /></button>
              <button className="k-btn-icon" style={{ padding: 6, color: 'var(--k-danger)' }} onClick={() => handleDelete(cat)}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Category form modal */}
      {showForm && (
        <div className="k-modal-overlay">
          <div className="k-modal k-modal-sm">
            <div className="k-modal-header">
              <div className="k-modal-title">{editCat ? 'Edit Category' : 'Add Category'}</div>
              <button className="k-btn-icon" onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <div className="k-modal-body">
              <div className="k-form-group">
                <label className="k-label required">Category Name</label>
                <input className="k-input" placeholder="e.g. Biryani" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
              </div>
              <div className="k-form-group">
                <label className="k-label">Description</label>
                <textarea className="k-textarea" placeholder="Optional description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
            </div>
            <div className="k-modal-footer">
              <button className="k-btn k-btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="k-btn k-btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} /> : (editCat ? 'Save' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MenuPage() {
  return (
    <Routes>
      <Route index element={<MenuOverview />} />
      <Route path="add" element={<MenuItemForm mode="add" />} />
      <Route path="edit/:itemId" element={<MenuItemForm mode="edit" />} />
      <Route path="categories" element={<ManageCategories />} />
      <Route path="bulk-upload" element={<BulkUploadPage />} />
    </Routes>
  );
}
